import React, { useEffect } from "react";
import container from "../../../../styles/Global.module.css";
import { compaoProductStatus } from "../../../../json/StatusOptions.js";
import ColumnStatus from "../../../../components/global/fields/ColumnStatus.jsx";
import UsersSelect from "../../../../components/global/fields/UsersSelect.jsx";
import EditableText from "../../../../components/global/fields/EditableText.jsx";
import { editBoardField, getStatus } from "../../../../utils/ApiCall.js";
import BaseBoard from "../../../global/BoardTemplate/BaseBoard.jsx";
import { GridLogicOperator } from "@mui/x-data-grid-pro";
import { useCustomState } from "../../../../utils/useCustomState.js";
import EditableField from "../../../../components/global/fields/EditableFieldNumber.jsx";
import SingleDuration from "../../../../components/global/fields/SingleDurationDatePicker.jsx";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const CompaoProduct = () => {
  const apiUrl = `${backendUrl}/api/compao-product/items`;
  const apiUsers = `${backendUrl}/api/users`;

  const [state, updateState] = useCustomState({
    status: "To do",
    groups: ["To do", "Working on it", "Backlog", "Done", "Stuck"],
    users: [],
    groupedItems: {},
    selectionRow: [],
    updateRows: [],
    columnsFilter: { id: false },
    viewFilter: {
      items: [],
      quickFilterLogicOperator: GridLogicOperator.Or,
    },
    pinnedColumns: {
      left: ["__check__", "threads"],
      right: [],
    },
    refreshState: false,
  });

  useEffect(() => {
    const fetchApi = async () => {
      const resUsers = await fetch(apiUsers);
      const data = await resUsers.json();
      updateState({ users: data });
    };
    fetchApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterItemsAndSetGroups = (items) => {
    const groupedItems = {};
    state.groups.forEach((group) => {
      groupedItems[group] = items.filter((item) => item.status === group);
    });
    updateState({ groupedItems });
  };

  const handleEditResponseChange = async (newValue, rowID, field) => {
    let status;
    if (field !== "status") {
      status = await getStatus(rowID, "compao-product");
    } else {
      status = newValue;
    }

    const updatedGroupedItems = { ...state.groupedItems };

    // Buscamos en qué grupo está actualmente
    const oldGroup = state.groups.find((group) =>
      state.groupedItems[group]?.some((item) => +item.id === +rowID)
    );

    if (!oldGroup) return;

    // Sacamos el item del grupo actual
    updatedGroupedItems[oldGroup] = updatedGroupedItems[oldGroup].filter(
      (item) => +item.id !== +rowID
    );

    // Actualizamos el item con el nuevo valor
    const updatedItem = {
      ...(state.groupedItems[oldGroup].find((item) => +item.id === +rowID) ||
        {}),
      [field]: newValue,
      status,
    };

    // Lo metemos en el nuevo grupo
    if (!updatedGroupedItems[status]) updatedGroupedItems[status] = [];
    updatedGroupedItems[status].push(updatedItem);

    updateState({ groupedItems: updatedGroupedItems });
  };

  const updateBoard = (newData) => {
    const updatedRows = [...state.updateRows, newData];
    updateState({ updateRows: updatedRows });
  };

  const getGroupRows = (groupName) => {
    return state.groupedItems?.[groupName] || [];
  };

  const controlEdit = async (params) => {
    let ids =
      state.selectionRow.length !== 0 ? state.selectionRow : [params.id];

    switch (params.field) {
      case "points":
        await editBoardField(
          "/compao-product",
          ids,
          params.field,
          state.points
        );
        updateState({ refreshState: true });
        break;
      default:
        break;
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 70,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "name",
      headerName: "Elemento",
      width: 300,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <EditableText
          {...params}
          field={"name"}
          handleEditResponseChange={handleEditResponseChange}
          initialValue={params.row.name}
        />
      ),
    },
    {
      field: "userStory",
      headerName: "Historia de Usuario",
      width: 300,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <EditableText
          {...params}
          field={"userStory"}
          handleEditResponseChange={handleEditResponseChange}
          initialValue={params.row.userStory}
        />
      ),
    },
    {
      field: "acceptanceCriteria",
      headerName: "Criterios de Aceptación",
      width: 300,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <EditableText
          {...params}
          field={"acceptanceCriteria"}
          handleEditResponseChange={handleEditResponseChange}
          initialValue={params.row.acceptanceCriteria}
        />
      ),
    },
    {
      field: "points",
      headerName: "Puntos",
      width: 180,
      headerAlign: "center",
      align: "center",
      renderEditCell: (params) => (
        <EditableField
          {...params}
          onStateChange={(value) => {
            updateState({ points: value });
          }}
        />
      ),
      editable: true,
    },
    {
      field: "responsibles",
      headerName: "Person",
      width: 140,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <UsersSelect
          {...params}
          users={state.users}
          field={"responsibles"}
          handleEditResponseChange={handleEditResponseChange}
        />
      ),
    },
    {
      field: "deadline",
      headerName: "Deadline",
      width: 160,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <SingleDuration
          {...params}
          selectionRow={state.selectionRow}
          fieldKey="deadline"
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      cellClassName: "no-padding",
      headerAlign: "center",
      renderCell: (params) => (
        <ColumnStatus
          {...params}
          selectionRow={state.selectionRow}
          field={"status"}
          columnPosition={280}
          handleEditResponseChange={handleEditResponseChange}
          statusOption={compaoProductStatus}
        />
      ),
    },
  ];

  return (
    <div className={container.globalContainer}>
      <BaseBoard
        columns={columns}
        groups={state.groups}
        getGroupRows={getGroupRows}
        setGroupItems={filterItemsAndSetGroups}
        apiUrl={apiUrl}
        status={state.status}
        setStatus={(status) => updateState({ status })}
        updateBoard={updateBoard}
        refreshState={state.refreshState}
        updateRows={state.updateRows}
        setSelectionRow={(selectionRow) => updateState({ selectionRow })}
        selectionRow={state.selectionRow}
        viewFilter={state.viewFilter}
        setViewFilter={(viewFilter) => updateState({ viewFilter })}
        columnsFilter={state.columnsFilter}
        setColumnsFilter={(columnsFilter) => updateState({ columnsFilter })}
        pinnedColumns={state.pinnedColumns}
        setPinnedColumns={(pinnedColumns) => updateState({ pinnedColumns })}
        checkboxSelection={true}
        height={"80vh"}
        maxHeight={"80vh"}
        controlEdit={controlEdit}
      />
    </div>
  );
};

export default CompaoProduct;
